/**
 * Catálogo de módulos do Sistema de Templates de Tenant.
 *
 * Lista os adaptadores disponíveis, suas dependências e as ações de
 * conflito permitidas. Usado tanto na UI quanto nos processos server-side.
 *
 * @version 1.0.0
 */

import type { TemplateModuleAdapter } from './types';
import type { TemplateModuleKey } from './constants';

/**
 * Catálogo canônico de módulos suportados na versão inicial.
 *
 * Nota: os módulos removidos do escopo inicial (WhatsApp, Campanhas,
 * Financeiro, IA, Wiki, White Label, Integrações/Webhooks, Canais/Webchat
 * e configurações gerais de empresa) não aparecem neste catálogo.
 */
export const TEMPLATE_MODULE_CATALOG: Record<TemplateModuleKey, TemplateModuleAdapter> = {
  crm: {
    key: 'crm',
    name: 'CRM',
    description: 'Workspaces, pipelines, fases, origens, motivos de perda, produtos, tabelas de preço e playbooks.',
    version: '1.0.0',
    dependencies: ['permissions'],
    optionalDependencies: ['dashboards'],
    conflictStrategies: ['reuse_existing', 'create_new', 'update_safe_fields', 'skip', 'fail_on_conflict'],
    exportable: true,
    importable: true,
  },
  contracts: {
    key: 'contracts',
    name: 'Contratos',
    description: 'Modelos de contrato, versões, tipos e requisitos documentais.',
    version: '1.0.0',
    dependencies: ['crm'],
    optionalDependencies: [],
    conflictStrategies: ['reuse_existing', 'create_new', 'update_safe_fields', 'skip', 'fail_on_conflict'],
    exportable: true,
    importable: true,
  },
  scheduling: {
    key: 'scheduling',
    name: 'Agendamento',
    description: 'Tipos de evento, perguntas e disponibilidade semanal.',
    version: '1.0.0',
    dependencies: ['crm'],
    optionalDependencies: ['chat'],
    conflictStrategies: ['reuse_existing', 'create_new', 'update_safe_fields', 'skip', 'fail_on_conflict'],
    exportable: true,
    importable: true,
  },
  chat: {
    key: 'chat',
    name: 'Chat e Atendimento',
    description: 'Departamentos, grupos, etiquetas, Kanban de conversas, filas e regras de distribuição.',
    version: '1.0.0',
    dependencies: ['permissions'],
    optionalDependencies: [],
    conflictStrategies: ['reuse_existing', 'create_new', 'update_safe_fields', 'skip', 'fail_on_conflict'],
    exportable: true,
    importable: true,
  },
  tasks: {
    key: 'tasks',
    name: 'Tarefas, Retenção e Jurídico',
    description: 'Categorias, fases, classificações, motivos de conclusão e modelos de checklist.',
    version: '1.0.0',
    dependencies: ['permissions'],
    optionalDependencies: ['crm'],
    conflictStrategies: ['reuse_existing', 'create_new', 'update_safe_fields', 'skip', 'fail_on_conflict'],
    exportable: true,
    importable: true,
  },
  flows: {
    key: 'flows',
    name: 'Fluxos',
    description: 'Definições, nodes, edges e variáveis declaradas dos fluxos legados.',
    version: '1.0.0',
    dependencies: ['crm', 'chat'],
    optionalDependencies: ['tasks'],
    conflictStrategies: ['reuse_existing', 'create_new', 'update_safe_fields', 'skip', 'fail_on_conflict'],
    exportable: true,
    importable: true,
  },
  workflow: {
    key: 'workflow',
    name: 'Workflow 2.0',
    description: 'Definições, versões, nodes, edges e entry gates do motor de orquestração.',
    version: '1.0.0',
    dependencies: ['permissions'],
    optionalDependencies: ['crm', 'chat', 'tasks'],
    conflictStrategies: ['reuse_existing', 'create_new', 'update_safe_fields', 'skip', 'fail_on_conflict'],
    exportable: true,
    importable: true,
  },
  reminders: {
    key: 'reminders',
    name: 'Lembretes e Notificações',
    description: 'Regras, templates e limites funcionais sem destinatários.',
    version: '1.0.0',
    dependencies: ['permissions'],
    optionalDependencies: ['chat'],
    conflictStrategies: ['reuse_existing', 'create_new', 'update_safe_fields', 'skip', 'fail_on_conflict'],
    exportable: true,
    importable: true,
  },
  dashboards: {
    key: 'dashboards',
    name: 'Dashboards',
    description: 'Definições de layout e filtros sem valores pessoais.',
    version: '1.0.0',
    dependencies: ['permissions'],
    optionalDependencies: ['crm', 'tasks'],
    conflictStrategies: ['reuse_existing', 'create_new', 'update_safe_fields', 'skip', 'fail_on_conflict'],
    exportable: true,
    importable: true,
  },
  referral: {
    key: 'referral',
    name: 'Indicações',
    description: 'Programas, campanhas e materiais sem arquivos sensíveis.',
    version: '1.0.0',
    dependencies: ['crm', 'permissions'],
    optionalDependencies: ['contracts'],
    conflictStrategies: ['reuse_existing', 'create_new', 'update_safe_fields', 'skip', 'fail_on_conflict'],
    exportable: true,
    importable: true,
  },
  vehicles: {
    key: 'vehicles',
    name: 'Veículos',
    description: 'Categorias e parâmetros funcionais de veículos.',
    version: '1.0.0',
    dependencies: ['permissions'],
    optionalDependencies: ['crm'],
    conflictStrategies: ['reuse_existing', 'create_new', 'update_safe_fields', 'skip', 'fail_on_conflict'],
    exportable: true,
    importable: true,
  },
  permissions: {
    key: 'permissions',
    name: 'Permissões',
    description: 'Perfis de permissão por menu e fase; usuários não são clonados.',
    version: '1.0.0',
    dependencies: [],
    optionalDependencies: [],
    conflictStrategies: ['reuse_existing', 'create_new', 'update_safe_fields', 'skip', 'fail_on_conflict'],
    exportable: true,
    importable: true,
  },
};

/**
 * Retorna os módulos em ordem topológica de aplicação.
 *
 * Implementa uma ordenação simples baseada nas dependências declaradas.
 * Módulos sem dependências vêm primeiro; módulos com dependências só
 * aparecem depois que todas as suas dependências já foram posicionadas.
 *
 * RELEASE: 2026-09-08 v1.0.1 - Detecção de ciclos (dependências opcionais
 *          mútuas, ex.: crm <-> dashboards) que causavam recursão infinita
 *          ("Maximum call stack size exceeded") ao abrir a tela.
 */
export function getTemplateModulesInApplyOrder(): TemplateModuleKey[] {
  const visited = new Set<TemplateModuleKey>();
  // Módulos cuja visita está em andamento; usado para cortar ciclos.
  const emProcessamento = new Set<TemplateModuleKey>();
  const order: TemplateModuleKey[] = [];

  function visit(key: TemplateModuleKey) {
    if (visited.has(key)) return;
    // Ciclo detectado: já estamos dentro da visita deste módulo.
    if (emProcessamento.has(key)) return;

    const adapter = TEMPLATE_MODULE_CATALOG[key];
    if (!adapter) return;

    emProcessamento.add(key);

    for (const dep of adapter.dependencies) {
      visit(dep);
    }
    for (const dep of adapter.optionalDependencies) {
      visit(dep);
    }

    emProcessamento.delete(key);
    visited.add(key);
    order.push(key);
  }

  for (const key of Object.keys(TEMPLATE_MODULE_CATALOG) as TemplateModuleKey[]) {
    visit(key);
  }

  return order;
}


/**
 * Verifica se um conjunto de módulos selecionados satisfaz todas as
 * dependências obrigatórias dos módulos escolhidos.
 */
export function validateModuleSelection(selected: TemplateModuleKey[]): {
  valid: boolean;
  missing: Array<{ module: TemplateModuleKey; missingDependency: TemplateModuleKey }>;
} {
  const selectedSet = new Set(selected);
  const missing: Array<{ module: TemplateModuleKey; missingDependency: TemplateModuleKey }> = [];

  for (const key of selected) {
    const adapter = TEMPLATE_MODULE_CATALOG[key];
    if (!adapter) continue;

    for (const dep of adapter.dependencies) {
      if (!selectedSet.has(dep)) {
        missing.push({ module: key, missingDependency: dep });
      }
    }
  }

  return { valid: missing.length === 0, missing };
}
